import jsPDF from "jspdf";
import "jspdf-autotable";
import api from "../../helpers/util";
import moment from "moment";

const CustomerReportPDF = async ({
  orders,
  columns,
  isCustomerEnabled = false,
  isOriginEnabled = false,
  filename,
  send = false,
  setSendingEmails,
  setMsg,
  setIsMsg,
  customers = [],
  origins = [],
  setErr,
  setSendResults,
  pdfContainerRef = null,
  selectedCompany = null,
  companies = [],
}) => {

  const sendEmail = async (formData) => {
    setSendingEmails(true);
    try {
      const response = await api.post("/send-email", formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      console.log("Email sent response:", response.data);
      if (setSendResults) setSendResults(response.data.sent || []);
      setIsMsg(true);
      setMsg(`Sent ${response.data.sent?.length ?? 0} email(s) successfully`);
      setErr(false);
    } catch (error) {
      console.error("Error sending email:", error);
      setIsMsg(true);
      setMsg(error.response?.data?.message || error.message || "Failed to send email");
      setErr(true);
    } finally {
      setSendingEmails(false);
    }
  };

  if (!orders || orders.length < 1) {
    const doc = new jsPDF({ orientation: "portrait", unit: "pt", format: "legal" });
    doc.text("No orders available", 40, 50);
    const blobURL = doc.output("bloburl");
    pdfContainerRef.current.innerHTML = `<iframe src="${blobURL}" width="100%" height="600px"></iframe>`;
    return;
  }

  const companyObj = companies.find((c) => c.id === selectedCompany);
  const fallbackEmail = companyObj?.emailaddress || "bala@acumenautomation.com.au";

  const indexHscw = columns.findIndex((c) => c.value === "hscw");

  const uniqueSpeciesInData = new Set(orders.map((o) => o.species));
  const baseSpecies = ["OVINE", "BOVINE", "PORCINE"];
  const extraSpecies = ["GOAT", "OTHER"].filter((s) => uniqueSpeciesInData.has(s));
  const speciesList = [...baseSpecies, ...extraSpecies];

  // Origin checkbox → group by origin, send to origin recipients
  // Customer checkbox or neither → group by customer, send to customer recipients
  const useOriginMode = isOriginEnabled;
  const groupKey = useOriginMode ? "origin" : "owner";
  const groupLabel = useOriginMode ? "Origin" : "Customer";
  // Sub-label is the opposite dimension
  const subLabel = useOriginMode ? "Customer" : "Origin";
  const subKey = useOriginMode ? "owner" : "origin";

  // Resolve every recipient email for a customer/origin: legacy single field
  // (customer.emailaddress / origin.email) PLUS any contacts flagged
  // includeinemail = 1 in customercontact / origincontact.
  const multipleRecipients = (groupName) => {
    const entity = useOriginMode
      ? origins.find((o) => o.name.trim() === groupName)
      : customers.find((c) => c.name.trim() === groupName);

    if (!entity) return [fallbackEmail];

    const contactEmails = (entity.contacts || [])
      .filter((c) => c.includeinemail && c.email)
      .map((c) => c.email.trim());

    const legacyEmail = (entity.emailaddress || entity.email || "").trim();

    const all = [...new Set(
      [...(legacyEmail ? [legacyEmail] : []), ...contactEmails].map((e) => e.toLowerCase())
    )];

    return all.length ? all : [fallbackEmail];
  };

  // Group by unique RECIPIENT SET first (bodies going to the same set of
  // addresses share a single PDF + single email send).
  const emailGroups = {};

  for (const order of orders) {
    const groupName = order[groupKey]?.trim() || "Unknown";
    const emails = multipleRecipients(groupName);
    const emailKey = [...emails].sort().join(",");

    if (!emailGroups[emailKey]) {
      emailGroups[emailKey] = { emails, sections: {} };
    }
    if (!emailGroups[emailKey].sections[groupName]) {
      emailGroups[emailKey].sections[groupName] = [];
    }
    emailGroups[emailKey].sections[groupName].push(order);
  }

  // Build PDF for each unique recipient set
  const buildEmailPDF = (emailData, targetDoc) => {
    const productionDate = Object.values(emailData.sections).flat()[0]?.production_date
      ? moment(Object.values(emailData.sections).flat()[0].production_date).format("YYYY-MM-DD")
      : moment().format("YYYY-MM-DD");

    const makeDidDrawPage = (doc, sectionName) => (data) => {
      const pageWidth = doc.internal.pageSize.getWidth();
      const pageHeight = doc.internal.pageSize.getHeight();
      doc.setFontSize(14);
      doc.text(`Processing Summary for ${productionDate}`, data.settings.margin.left, 30);
      doc.setFontSize(16);
      doc.text(`${groupLabel}: ${sectionName}`, data.settings.margin.left, 50);
      doc.setFontSize(10);
      doc.text("Powered by Meatworx", data.settings.margin.left, pageHeight - 10);
      doc.text(
        `Page ${doc.internal.getCurrentPageInfo().pageNumber}`,
        pageWidth - 60,
        pageHeight - 10
      );
    };

    let isFirstPage = true;

    Object.entries(emailData.sections).forEach(([sectionName, sectionOrders]) => {
      // Within each section, group by the sub-dimension (origin or customer)
      const subGroups = {};
      for (const order of sectionOrders) {
        const key = order[subKey]?.trim() || "Unknown";
        if (!subGroups[key]) subGroups[key] = [];
        subGroups[key].push(order);
      }

      Object.entries(subGroups).forEach(([subName, subOrders]) => {
        speciesList.forEach((species) => {
          const speciesOrders = subOrders.filter((o) => o.species === species);
          if (speciesOrders.length === 0) return;

          const tableData = speciesOrders.map((order, index) => ({
            index: index + 1,
            body_number: order.body_number,
            owner: order.owner,
            origin: order.origin,
            sex: order.sex,
            category: order.category,
            hscw: order.hscw,
            fat_score: order.fat_score || "0.00",
            production_date: moment(order.production_date).format("YYYY-MM-DD"),
            species: order.species,
          }));

          const totalWeight = speciesOrders
            .reduce((sum, o) => sum + (parseFloat(o.hscw) || 0), 0)
            .toFixed(2);

          if (!isFirstPage) targetDoc.addPage();
          isFirstPage = false;

          // Teal sub-label (Origin or Customer) below the header
          targetDoc.setFontSize(12);
          targetDoc.setTextColor(0, 184, 148);
          targetDoc.text(`${subLabel}: ${subName}`, 30, 65);
          targetDoc.setTextColor(0, 0, 0);

          targetDoc.autoTable({
            head: [columns.map((col) => col.name)],
            body: tableData.map((row) => columns.map((col) => row[col.value])),
            foot: [
              columns.map((col, index) => {
                if (col.value !== "hscw" && index !== indexHscw - 1) return "";
                else if (index === indexHscw - 1) return "Total Weight:";
                else return totalWeight + " kgs";
              }),
            ],
            startY: 75,
            styles: { fontSize: 8 },
            theme: "grid",
            margin: { top: 60, left: 30, right: 30 },
            didDrawPage: makeDidDrawPage(targetDoc, sectionName),
          });
        });
      });
    });
  };

  //Preview mode
  if (!send) {
    const doc = new jsPDF({ orientation: "portrait", unit: "pt", format: "legal" });
    let isFirstEmail = true;

    Object.values(emailGroups).forEach((emailData) => {
      if (!isFirstEmail) doc.addPage();
      isFirstEmail = false;
      buildEmailPDF(emailData, doc);
    });

    const blobURL = doc.output("bloburl");
    pdfContainerRef.current.innerHTML = `<iframe src="${blobURL}#toolbar=0" width="100%" height="600px"></iframe>`;
    return;
  }

  // Send mode: one PDF + one email per unique recipient set
  const pdfBlobs = [];
  const recipients = []; // array of arrays — one recipient-set (array of emails) per PDF
  const ownerNames = [];

  for (const [emailKey, emailData] of Object.entries(emailGroups)) {
    const singleDoc = new jsPDF({ orientation: "portrait", unit: "pt", format: "legal" });
    buildEmailPDF(emailData, singleDoc);

    const firstSectionName = Object.keys(emailData.sections)[0];
    const safeFilename = `orders_${firstSectionName.split(" ")[0].toLowerCase()}_${moment().format("YYYYMMDD")}.pdf`;

    pdfBlobs.push({ blob: singleDoc.output("blob"), name: safeFilename });
    recipients.push(emailData.emails);
    ownerNames.push(Object.keys(emailData.sections).join(", "));

    console.log(
      `${groupLabel}(s): ${Object.keys(emailData.sections).join(", ")} → ${emailData.emails.join(", ")} (${Object.values(emailData.sections).flat().length} animals)`
    );
  }

  const formData = new FormData();
  pdfBlobs.forEach((pdf) => formData.append("pdfs", pdf.blob, pdf.name));
  formData.append("subject", "Summary of orders");
  formData.append("recipients", JSON.stringify(recipients));
  formData.append("company_name", companyObj?.name || "");
  formData.append("owner_names", JSON.stringify(ownerNames));

  console.log("=== SENDING EMAILS ===");
  console.log("Mode:", useOriginMode ? "Origin" : "Customer");
  console.log("Recipient sets:", recipients);
  console.log("PDF count:", pdfBlobs.length);

  await sendEmail(formData);
};

export default CustomerReportPDF;